import type { ReactNode } from "react";
import { CircleCheck, CircleX } from "lucide-react";

import type { GuardCheck } from "@/lib/integrations/guards";
import { extraCounts, when } from "@/lib/integrations/words";

import styles from "./integrations.module.css";
import { OutcomeBar } from "./OutcomeBar";

/**
 * What a run of a version did, or would do: outcome counts, the guards, and
 * the rows of one outcome with their reasons.
 */
export function TryReport({
  title,
  rowsCollected,
  startedAt,
  timeZone,
  counts,
  checks,
  notes,
  onPick,
  children,
}: {
  title: string;
  rowsCollected: number;
  startedAt: string | null;
  timeZone?: string;
  counts: Record<string, number>;
  checks: GuardCheck[];
  notes?: string | null;
  onPick?: (outcome: string) => void;
  /** The rows of the picked outcome. */
  children?: ReactNode;
}) {
  const extra = extraCounts(counts);
  return (
    <div style={{ display: "grid", gap: "var(--s4)" }}>
      <div>
        <p style={{ margin: "0 0 var(--s2)" }}>
          <strong>{title}</strong>{" "}
          <span className={styles.muted}>
            Run of {when(startedAt, timeZone)} · {rowsCollected.toLocaleString()} rows
          </span>
        </p>
        {rowsCollected === 0 ? (
          <p className={styles.warnText}>This run has no rows. Pick another run or check the job.</p>
        ) : (
          <OutcomeBar counts={counts} onPick={onPick} />
        )}
        {extra.length > 0 && <p className={styles.faint} style={{ margin: "var(--s1) 0 0" }}>Records: {extra.join(" · ")}</p>}
      </div>
      <div>
        <div className={styles.muted} style={{ fontWeight: 600, marginBottom: "var(--s1)" }}>
          Guards
        </div>
        <ul className={styles.lines} style={{ listStyle: "none", paddingLeft: 0 }}>
          {checks.map((check) => (
            <li key={check.key} className={styles.inline} title={check.detail}>
              <span className={check.passed ? styles.goodText : styles.badText}>
                {check.passed ? <CircleCheck size={15} aria-label="passed" /> : <CircleX size={15} aria-label="would trip" />}
              </span>
              <span>{check.label}</span>
              <span className={styles.faint}>{check.detail}</span>
            </li>
          ))}
        </ul>
      </div>
      {notes && (
        <div>
          <div className={styles.muted} style={{ fontWeight: 600, marginBottom: "var(--s1)" }}>
            What the workflow said
          </div>
          <p className={`${styles.wrap} ${styles.muted}`} style={{ margin: 0 }}>
            {notes}
          </p>
        </div>
      )}
      {children}
    </div>
  );
}
